import React, { useEffect, useState } from "react"
import {
  Box,
  Typography,
  ToggleButton,
  ToggleButtonGroup,
  TextField,
  Checkbox,
  FormControlLabel,
  MenuItem,
} from "@mui/material"
import TextEditor from "../editor/TextEditor"
import MultiSelectChipsBelow from "./MultiSelectChipsBelow"
import { fetchPartnerList } from "../../api/courseApi"
import ValidationErrorText from "../common/ValidationErrorText"
import { defaultEmiFinancialAidVal } from "../../containers/course-container/reducer/courseFieldsDefaultValue"

const tenureList = [
  "3 Months", "6 Months", "9 Months", "12 Months", "18 Months", "24 Months", "30 Months", "36 Months", "45 Months", "54 Months"
]

const inputStyle = {
  height: 38,
  '& .MuiInputBase-input': {
    padding: '8px 12px',
    fontSize: '13px',
  },
}

const EmiFinancialAidForm = ({ label, value = {}, onChange, readOnly = false,fieldErrors,fieldError,fieldType }) => {
  const {
    emiEnabled = "yes",
    zeroInterest = false,
    tenure = "",
    partnersId=[],
    overview = "",
  } = value

  const [partnersList, setPartnersList] = useState([])
  const [loadingPartners, setLoadingPartners] = useState(false)

  useEffect(() => {
    fetchPartnerList(setPartnersList, setLoadingPartners)
  }, [])

  const handleChange = (field, newValue) => {
    if (readOnly) return
    onChange({ ...value, [field]: newValue })
  }

  return (
    <Box sx={{ width: "100%" }}>
      <Typography fontSize="14px" fontWeight="500" mb={1}>
        {label}
      </Typography>

      <ToggleButtonGroup
        value={emiEnabled}
        exclusive
        onChange={(_, newValue) => {
          if (readOnly) return
          if (newValue !== null) handleChange("emiEnabled", newValue)
        }}
        sx={{
          mb: 2,
          display: "flex",
          borderRadius: "30px",
          overflow: "hidden",
          padding: '2px',
          border: "1px solid #ccc",
          width: "fit-content",
        }}
        disabled={readOnly}
      >
        <ToggleButton value="yes" sx={toggleBtnStyles} disabled={readOnly}>Yes</ToggleButton>
        <ToggleButton value="no" sx={toggleBtnStyles} disabled={readOnly}>No</ToggleButton>
      </ToggleButtonGroup>

      {/* Only show below if emiEnabled is "yes" */}
      {emiEnabled === "yes" && (
        <>
          <MultiSelectChipsBelow
            label="Partners with"
            placeholder="Select Partner(s)"
            options={partnersList}
            value={partnersId}
            onChange={newValue => handleChange("partnersId", newValue)}
            readOnly={readOnly}
          />
          <ValidationErrorText error={fieldErrors?.[`${fieldType}.partnersId`]}/>  

          <Typography variant="body2" sx={{ fontWeight: 500, mb: 0.5, color: '#15243E' }}>
            Overview
          </Typography>
          <Typography variant="body2" sx={{ fontWeight: 400, mb: 0.5, fontSize: '12px', color: '#6E7485' }}>
            Kindly share the EMI information.
          </Typography>
          <TextEditor
            value={overview}
            onChange={text => handleChange("overview", text)}
            placeholder="Write an overview about EMI options and process"
            readOnly={readOnly}
          />
          <ValidationErrorText error={fieldErrors?.[`${fieldType}.overview`]}/>  

          <FormControlLabel
            control={
              <Checkbox
                checked={zeroInterest}
                onChange={e => {
                  if (readOnly) return;
                  const checked = e.target.checked;
                  const newValue = { ...value, zeroInterest: checked };
                  if (!checked) newValue.tenure = "";
                  onChange(newValue);
                }}
                disabled={readOnly}
              />
            }
            label="Zero Interest EMI Cost"
            sx={{
              mb: 2,
              '& .MuiFormControlLabel-label': {
                fontSize: '13px',
                fontWeight: '500',
              },
            }}
          />

          {zeroInterest && (
            <>
              <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5, fontSize: '13px' }}>
                Maximum Zero Interest EMI Duration
              </Typography>
              <TextField
                select
                fullWidth
                value={tenure}
                onChange={e => handleChange("tenure", e.target.value)}
                InputProps={{ sx: inputStyle, readOnly }}
                SelectProps={{
                  displayEmpty: true,
                  renderValue: (selected) => {
                    if (!selected) {
                      return <em style={{ color: "#aaa" }}>Select tenure</em>
                    }
                    return selected
                  },
                }}
                disabled={readOnly}
              >
                <MenuItem disabled value="">
                  <em>Select tenure</em>
                </MenuItem>
                {tenureList.map((option) => (
                  <MenuItem key={option} value={option} sx={{ fontSize: '13px' }}>
                    {option}
                  </MenuItem>
                ))}
              </TextField>
             <ValidationErrorText error={fieldErrors?.[`${fieldType}.tenure`]}/>  
              
            </>
          )}
        </>
      )}
    </Box>
  )
}

// Button styles for both toggle buttons
const toggleBtnStyles = {
  flex: 1,
  px: 1,
  py: '0',
  fontSize: '13px',
  minWidth: 'auto',
  borderRadius: '30px !important',
  textTransform: "none",
  fontWeight: "500",
  transition: "0.3s",
  border: 'none',
  "&.Mui-selected": {
    backgroundColor: "#1075FF",
    color: "#fff",
    "&:hover": {
      backgroundColor: "#1565c0",
    },
  },
}

export default EmiFinancialAidForm
